/** Vstupní bod plochy: načte layout (committed/draft), odvodí WS subscription, vyrenderuje strom.
 *  V edit módu (admin) přepne na draft + zobrazí toolbar a panel editoru. */

import React, { useEffect, useMemo } from "react";
import { type LayoutDoc, walk } from "@globals/layouts";
import type { WidgetContext } from "@client/config/layoutWidgets";
import { useWsSubscription } from "@client/lib/wsSubscriptions";
import { useLayoutStore } from "@client/store/layout";
import { useLayoutEditor } from "@client/store/layoutEditor";
import { useUIStore } from "@client/store/ui";
import { useUserStore } from "@client/store/user";
import { EditCanvas } from "./editor/EditCanvas";
import { LayoutNodeView } from "./LayoutNodeView";
import { LayoutUnavailable } from "./LayoutUnavailable";

const EMPTY_DOC: LayoutDoc = {
  schemaVersion: 1,
  root: { kind: "container", id: "root", dir: "col", gap: 12, padding: 12, align: "stretch", justify: "start", wrap: false, children: [] },
};

const collectTopics = (doc: LayoutDoc, slug?: string): string[] => {
  const set = new Set<string>();
  const add = (t: unknown) => {
    if (typeof t === "string" && t) set.add(slug ? t.replace(/\{slug\}/g, slug) : t);
  };
  walk(doc.root, (n) => {
    if (n.kind !== "widget") return;
    add(n.config?.topicId);
    if (Array.isArray(n.config?.topics)) (n.config.topics as unknown[]).forEach(add);
  });
  return [...set];
};

export const LayoutRoot: React.FC<{ layoutKey: string; context?: WidgetContext }> = ({ layoutKey, context }) => {
  const stored = useLayoutStore((s) => s.byKey.get(layoutKey));
  const status = useLayoutStore((s) => s.status);
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";
  const editMode = useUIStore((s) => s.editMode);
  const draftKey = useLayoutEditor((s) => s.draftKey);
  const draftDoc = useLayoutEditor((s) => s.doc);
  const beginEdit = useLayoutEditor((s) => s.beginEdit);
  const stop = useLayoutEditor((s) => s.stop);

  // `EMPTY_DOC` tu NENÍ náhrada obsahu — jen držák, aby hooky níž měly co číst, než se
  // pod nimi vykreslí `LayoutUnavailable`. Layout z kódu se nedosazuje, ten už neexistuje.
  const committed = stored?.doc ?? EMPTY_DOC;
  const wantEdit = editMode && isAdmin;

  // Init / teardown draftu pro tuto plochu.
  useEffect(() => {
    if (wantEdit) {
      const cur = useLayoutStore.getState().byKey.get(layoutKey);
      // Editovat nenačtenou nebo poškozenou plochu nedává smysl — uložením by se přepsala
      // prázdnotou. Editor se v takovém případě vůbec nezapne.
      if (cur && !cur.invalid && useLayoutEditor.getState().draftKey !== layoutKey) {
        beginEdit(layoutKey, cur.name, cur.doc);
      }
    } else if (useLayoutEditor.getState().draftKey === layoutKey) {
      stop();
    }
  }, [wantEdit, layoutKey, beginEdit, stop]);

  const editing = wantEdit && draftKey === layoutKey && !!draftDoc;
  const doc = editing && draftDoc ? draftDoc : committed;
  const slug = context?.slug;

  // Klíčujeme obsahem, ne identitou pole: nový `doc` se stejnými topicy nesmí vyrobit nový
  // `fragment` a znovu přihlašovat WS. Separátor \0 se v MQTT topicu vyskytnout nemůže.
  const topicsKey = useMemo(() => collectTopics(doc, slug).join("\0"), [doc, slug]);
  const fragment = useMemo(() => ({ mqttTopics: topicsKey === "" ? [] : topicsKey.split("\0") }), [topicsKey]);
  useWsSubscription(fragment);

  const scope: "dashboard" | "device" = layoutKey.startsWith("device:") ? "device" : "dashboard";
  const device = context?.deviceType;

  if (editing && draftDoc) {
    return <EditCanvas draftDoc={draftDoc} context={context} scope={scope} device={device} />;
  }

  // Až ZA hooky — dřívější return by porušil pravidla hooků (`useWsSubscription` výš).
  if (!stored || stored.invalid) {
    return <LayoutUnavailable layoutKey={layoutKey} status={status} invalid={stored?.invalid} />;
  }

  return <LayoutNodeView node={doc.root} ctx={context ?? {}} />;
};

export default LayoutRoot;
